import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, BadgeCheck, PackagePlus, Rocket } from "lucide-react";
import {
  contentFor,
  effectiveDiscountPercent,
  formatPrice,
  type Package,
} from "@nh/shared";
import { Alert, Button, Input } from "@/shared/ui/core";
import { cn } from "@/shared/lib/cn";
import { useAuth } from "@/features/auth/auth.context";
import { CartProvider } from "@/features/cart";
import { ShopLayout } from "@/features/shop/components/ShopLayout";
import { useShopCurrency } from "@/features/shop/useShopCurrency";
import { localeOf } from "@/features/shop/shop.catalog";
import { FRANCHISE_CONTENT } from "./franchise.content";
import { useQuery } from "@tanstack/react-query";
import { useWholesalePackages } from "./franchise.api";
import { WholesaleStockModal } from "./components/WholesaleStockModal";
import { fetchPackageBySlug } from "@/features/packages/packages.api";
import { ConFranquiciaResumen, PaqueteElegidoCard, precioConFranquicia } from "./components/PaqueteElegido";
import { writeFranchiseCheckout } from "./franchise.draft";
import { usePriceViewer } from "@/features/shop/usePriceViewer";
import { useFranchiseSettings } from "./useFranchiseSettings";

/**
 * ═══════════════════════════════════════════════════════════════════
 * Adquirir la franquicia — el panel al que manda la landing.
 *
 * El flujo de Anahí (26-ago), en este orden y sin más:
 *
 *  1. SOLO la ficha técnica de la franquicia, con el botón
 *     "Quiero iniciar".
 *  2. "Añade tu primer stock para tu tienda digital (paquetes
 *     mayoristas)" — el botón abre el MODAL de un solo tab.
 *  3. Elegido el paquete, se ve la suma (franquicia + paquete), se
 *     confirma la compra y se REDIRIGE al link de pago.
 *
 * Exige sesión: es lo que cierra el hueco documentado en la landing —
 * un pago de franquicia sin dueño no puede activar a nadie.
 * ═══════════════════════════════════════════════════════════════════
 */
/**
 * Cada pantalla pública monta su propio `CartProvider`: el armazón de la
 * tienda (`ShopLayout`) lo necesita para el contador del carrito.
 */
export function FranchiseAcquirePage() {
  return (
    <CartProvider>
      <FranchiseAcquireScreen />
    </CartProvider>
  );
}

/**
 * ── La compra pendiente sobrevive al login (Anahí, 27-ago) ──────────
 * "Esta compra ya debe estar guardada solo para cerrarse: una vez
 * iniciando sesión se abre de nuevo /franquicias/adquirir y se cierra
 * la venta." El avance (quiero-iniciar + paquete elegido) se guarda en
 * `localStorage`; al volver —del login, del registro o de mañana— la
 * pantalla retoma donde iba. Se limpia al confirmar.
 */
const ACQUIRE_STORAGE_KEY = "nh_franchise_acquire";

interface StoredAcquire {
  started: boolean;
  packageId?: string;
}

function readStoredAcquire(): StoredAcquire | null {
  try {
    const raw = window.localStorage.getItem(ACQUIRE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredAcquire;
    return typeof parsed?.started === "boolean" ? parsed : null;
  } catch {
    return null;
  }
}

function writeStoredAcquire(value: StoredAcquire | null): void {
  try {
    if (value === null) window.localStorage.removeItem(ACQUIRE_STORAGE_KEY);
    else window.localStorage.setItem(ACQUIRE_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Navegación privada: la compra sigue, solo no sobrevive al viaje.
  }
}

function FranchiseAcquireScreen() {
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const [currency, setCurrency] = useShopCurrency();
  const viewer = usePriceViewer();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const stored = readStoredAcquire();
  const [started, setStartedState] = useState(stored?.started ?? false);
  const [modalOpen, setModalOpen] = useState(false);
  const [stock, setStockState] = useState<Package | null>(null);
  const [phone, setPhone] = useState("");
  const [error, setError] = useState<string | null>(null);

  /*
   * El paquete guardado se restaura en cuanto la lista llega: solo se
   * guardó su id — el objeto fresco (precio incluido) viene del
   * catálogo, nunca del almacenamiento.
   */
  /*
   * ── El paquete que se eligió en `/paquetes` (Anahí, 29-ago) ────────
   * Llega por la URL (`?paquete=slug`) y NO por el almacenamiento: así
   * el enlace se puede compartir, y volver atrás y elegir otro cambia lo
   * que se ve. Se pide fresco al catálogo — precio, imagen y descuento
   * de franquiciatario tienen que ser los de hoy.
   */
  const [searchParams] = useSearchParams();
  const slugElegido = searchParams.get("paquete");
  const elegidoQuery = useQuery({
    queryKey: ["packages", "public", "slug", slugElegido],
    queryFn: () => fetchPackageBySlug(slugElegido as string),
    enabled: Boolean(slugElegido),
  });
  const elegido = elegidoQuery.data ?? null;
  /*
   * Solo un paquete MAYORISTA puede ser el primer stock de la
   * franquicia: el servidor rechaza los personales (ver
   * `order.service.ts`). Con uno personal elegido, la izquierda sigue
   * siendo el flujo de siempre —ficha y "quiero iniciar"— en vez de
   * ofrecer una compra conjunta que el servidor va a negar.
   */
  const elegidoEsStock = elegido?.category === "franchisee";

  const wholesale = useWholesalePackages();
  const storedPackageId = stored?.packageId;
  useEffect(() => {
    if (!storedPackageId || stock) return;
    const found = wholesale.data?.items.find((pkg) => pkg.id === storedPackageId);
    if (found) setStockState(found);
  }, [storedPackageId, stock, wholesale.data]);

  /*
   * Si lo que se eligió ya ES un paquete mayorista, ese ES el primer
   * stock: pedirlo otra vez en el modal sería preguntar dos veces lo
   * mismo. Un paquete personal no sirve de stock, así que ahí el flujo
   * de siempre sigue intacto.
   */
  useEffect(() => {
    if (!elegido || stock || elegido.category !== "franchisee") return;
    setStockState(elegido);
  }, [elegido, stock]);

  const setStarted = (value: boolean) => {
    setStartedState(value);
    writeStoredAcquire({ started: value, ...(stock ? { packageId: stock.id } : {}) });
  };

  const setStock = (pkg: Package | null) => {
    setStockState(pkg);
    writeStoredAcquire({ started: true, ...(pkg ? { packageId: pkg.id } : {}) });
  };

  // El precio del admin, el mismo que cobra el servidor (14-sep-2026).
  const franchiseSettings = useFranchiseSettings();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: "/franquicias/adquirir" }} />;
  }
  const fichaPriceCents = franchiseSettings.prices[currency];
  /*
   * "Franquicia incluida" (Anahí, 27-ago): con un paquete así, la
   * franquicia va a $0 y se cobra SOLO el paquete. El renglón se sigue
   * enseñando — se está adquiriendo, aunque no se cobre.
   */
  const franchisePriceCents = stock?.includesFranchise ? 0 : fichaPriceCents;
  // El primer stock va CON la franquicia: lleva el 10 % de franquiciatario.
  const stockPrice = stock ? precioConFranquicia(stock, currency) : undefined;
  const total =
    typeof stockPrice === "number" ? franchisePriceCents + stockPrice : undefined;

  /**
   * ═══════════════════════════════════════════════════════════════════
   * Las dos salidas terminan en /checkout (Anahí, 29-ago).
   *
   * "Cuando compren el paquete debe redirigir a /checkout para validar
   * la compra, los datos, la dirección y el tipo de pago. Dependiendo si
   * eligen la franquicia o el paquete."
   *
   * Antes cada botón cerraba la venta por su cuenta: este creaba el
   * pedido con la dirección que hubiera —o ninguna— y el de la derecha
   * abría Stripe sin preguntar nada. Dos caminos de pago distintos del
   * que usa el resto de la tienda, así que una compra de $15,072 se
   * cerraba con menos datos que una de $3,220. Ahora las dos dejan QUÉ
   * se compra y pasan por la pantalla que ya sabe pedir dirección,
   * teléfono y método, y que ya trata bien los tres métodos.
   * ═══════════════════════════════════════════════════════════════════
   */
  function irAlCheckout(pkg: Package, conFranquicia: boolean) {
    // Con franquicia en el pedido, o teniéndola ya, el paquete va con su
    // 10 % menos (14-sep). El servidor vuelve a hacer la cuenta igual.
    const precio =
      conFranquicia || viewer.isFranchisee
        ? (precioConFranquicia(pkg, currency) ?? pkg.prices[currency])
        : pkg.prices[currency];
    if (typeof precio !== "number") {
      setError(t("Ese paquete no tiene precio en esta moneda."));
      return;
    }

    writeFranchiseCheckout({
      packageId: pkg.id,
      packageSlug: pkg.slug,
      packageName: contentFor(pkg.translations, locale).name,
      packagePriceCents: precio,
      withFranchise: conFranquicia,
      // "Franquicia incluida": el renglón se enseña, pero a $0.
      franchisePriceCents: conFranquicia && !pkg.includesFranchise ? fichaPriceCents : 0,
      currency,
    });
    // La compra pendiente de ESTA pantalla ya cumplió: lo que manda a
    // partir de aquí es el borrador del checkout.
    writeStoredAcquire(null);
    navigate("/checkout");
  }

  function confirmar() {
    if (!stock) return;
    setError(null);
    irAlCheckout(stock, true);
  }

  const beneficios = FRANCHISE_CONTENT.content.beneficios;

  /*
   * ── El cierre de la venta, en UN solo sitio ────────────────────────
   * Teléfono + botón + la letra pequeña del pago manual. Vive aquí
   * porque ahora se pinta en DOS lugares distintos según cómo se llegó:
   * dentro de la ficha técnica cuando el paquete ya venía elegido, y
   * dentro de la tarjeta del primer stock cuando se elige a mano.
   * Duplicarlo era garantizar que un día cambiara en uno y no en el otro.
   */
  const confirmacion = (
    <>
      {/*
        ── El teléfono solo si NO lo tenemos (Anahí, 29-ago) ────────────
        "Quita, porque ya está guardado su número, no es necesario
        volverlo a pedir." Pedir un dato que ya está en la cuenta es
        trabajo que no compra nada, y encima invita a teclearlo distinto.
        El contrato SÍ lo exige (mínimo 7 dígitos), así que se sigue
        pidiendo a quien no lo tenga registrado: ahí no hay nada que
        heredar y sin él el pedido se rechaza.
      */}
      {!user?.phone && (
        <label className="grid max-w-xs gap-1 text-[12px] font-medium text-ds-ink-3">
          {t("Teléfono para contactarte")}
          <Input
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            placeholder="+52 55 1234 5678"
            inputMode="tel"
            autoComplete="tel"
          />
        </label>
      )}

      {error && (
        <Alert variant="error" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Button size="lg" onClick={confirmar}>
        {t("Confirmar compra e ir al pago")}
      </Button>
    </>
  );

  return (
    <ShopLayout currency={currency} onCurrencyChange={setCurrency}>
      {/*
        ── Dos columnas cuando se llega con un paquete (Anahí, 29-ago) ──
        Sin paquete elegido la pantalla es la de siempre, angosta y
        centrada. Con paquete, se ensancha y se parte: la franquicia a la
        izquierda y el paquete solo a la derecha, para poder COMPARAR sin
        hacer scroll entre una cifra y otra.
      */}
      <div
        className={
          elegido
            ? "mx-auto w-full max-w-[1180px] px-4 py-8 sm:px-8"
            : "mx-auto w-full max-w-[860px] px-4 py-8 sm:px-8"
        }
      >
        {/*
          Ya no hay confirmación aquí: el pedido se cierra en /checkout,
          que es donde vive la pantalla de "¡Pedido registrado!" con su
          folio y las instrucciones de cada método de pago.
        */}
        {(
          <>
            <Link
              to="/franquicias"
              className="inline-flex items-center gap-2 text-[13px] text-ds-ink-3 underline-offset-2 hover:underline"
            >
              <ArrowLeft className="h-4 w-4" />
              {t("Volver a Franquicias Digitales")}
            </Link>

            <div
              className={
                elegido
                  ? "mt-4 grid gap-5 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-stretch"
                  : ""
              }
            >
              {/*
                ── Las dos columnas, a la misma altura (Anahí, 29-ago) ───
                `items-stretch` en vez de `items-start`: las dos celdas
                crecen hasta la más alta, y dentro cada tarjeta es una
                columna flex con `flex-1` para ocupar lo que le tocó. Sin
                esto cada card medía lo suyo y quedaban desalineadas por
                abajo, que es lo que se veía.
              */}
              <div className="flex min-w-0 flex-col">
            {/* ── 1 · La ficha técnica ─────────────────────────────── */}
            <section
              className={cn(
                "rounded-3xl border border-ds-border bg-white p-6 shadow-ds-float sm:p-8",
                /*
                  El `mt-4` lo pone la REJILLA, no esta tarjeta (Anahí,
                  29-ago: "las cards deben estar alineadas"). Teniéndolo
                  las dos, la izquierda bajaba cuatro píxeles más que la
                  derecha y arrancaban a distinta altura.
                */
                elegidoEsStock ? "flex flex-1 flex-col" : "mt-4",
              )}
            >
              {/*
                ── Nombre a la izquierda, precio a la derecha (Anahí, 29-ago)
                Antes iban apilados y el precio quedaba solo, ocupando una
                línea entera para cuatro cifras. En una fila se leen como
                lo que son —qué es y cuánto cuesta— y el bloque se acorta,
                que es lo que permite que las dos columnas terminen a la
                misma altura. El rótulo "FICHA TÉCNICA" se fue: no
                aportaba nada que el título no dijera ya.

                `items-baseline` alinea el título con la cifra por su
                línea de texto, no por su caja: con tamaños tan distintos
                (26 px contra 32) centrarlos deja el precio flotando alto.
              */}
              <div className="flex items-baseline justify-between gap-x-6">
                <div className="min-w-0 flex-1">
                  {/*
                    ── La marca, en su propio renglón (Anahí, 29-ago) ───
                    En una sola línea el título se partía donde cabía y
                    dejaba "It's The / Best": el nombre de la marca roto
                    a la mitad. Partido a propósito, el corte cae donde
                    tiene sentido —el qué arriba, de quién abajo— y se
                    lee igual en cualquier ancho.
                  */}
                  <h1 className="text-[26px] font-semibold leading-tight text-ds-ink">
                    {t("Franquicia Digital")}
                    <span className="block">{t("It's The Best")}</span>
                  </h1>
                  <p className="mt-1 text-[15px] text-ds-ink-3">
                    {t("Tu negocio digital de wellness, con vigencia de un año.")}
                  </p>
                </div>
                <p className="shrink-0 whitespace-nowrap text-[clamp(22px,2.4vw,32px)] font-semibold leading-none text-ds-ink">
                  {formatPrice(fichaPriceCents, currency)}{" "}
                  <span className="text-[14px] font-normal text-ds-ink-3">
                    {currency} · {t("por año")}
                  </span>
                </p>
              </div>

              <ul className="mt-5 grid gap-2 sm:grid-cols-2">
                {beneficios.map((benefit) => (
                  <li key={benefit.title} className="flex items-start gap-2 text-[14px] text-ds-ink">
                    <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-ds-violet-pop" />
                    {t(benefit.title)}
                  </li>
                ))}
              </ul>

              {/*
                Con un paquete elegido, la ficha deja de ser un folleto y
                pasa a ser una CUENTA: cuánto costaría ese mismo paquete
                siendo franquiciatario y cuánto sale todo junto.
              */}
              {elegidoEsStock && elegido ? (
                /*
                  ── UNA sola card cuando el paquete ya viene elegido ───
                  Anahí (29-ago): "con esta card es suficiente y debe
                  estar ahí el botón". La segunda tarjeta —"añade tu
                  primer stock"— existía para ESCOGER el paquete, y aquí
                  ya está escogido: pedirlo otra vez era un paso que no
                  decide nada. Ficha, la cuenta y el cierre, seguido.
                */
                <>
                  <ConFranquiciaResumen
                    pkg={elegido}
                    currency={currency}
                    locale={locale}
                    franchiseCents={fichaPriceCents}
                  />
                  <div className="mt-5 grid gap-4">{confirmacion}</div>
                </>
              ) : (
                !started && (
                  <Button
                    size="lg"
                    leftIcon={<Rocket />}
                    className="mt-6"
                    onClick={() => setStarted(true)}
                  >
                    {t("Quiero iniciar")}
                  </Button>
                )
              )}
            </section>

            {/* ── 2 · El primer stock ──────────────────────────────── */}
            {started && !elegidoEsStock && (
              <section className="mt-4 rounded-3xl border border-ds-border bg-white p-6 shadow-ds-float sm:p-8">
                <h2 className="text-[19px] font-semibold text-ds-ink">
                  {t("Añade tu primer stock para tu tienda digital")}
                </h2>
                <p className="mt-1 text-[14px] text-ds-ink-3">
                  {t(
                    "Tu franquicia arranca con inventario: elige un paquete mayorista. Su costo se suma al de la franquicia en un solo pago.",
                  )}
                </p>

                {stock ? (
                  <StockElegido
                    stock={stock}
                    currency={currency}
                    locale={locale}
                    onChange={() => setModalOpen(true)}
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => setModalOpen(true)}
                    className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-ds-border p-6 text-[14px] font-medium text-ds-ink-3 transition hover:border-ds-violet-pop hover:text-ds-violet-pop"
                  >
                    <PackagePlus className="h-5 w-5" />
                    {t("Elegir mi paquete mayorista")}
                  </button>
                )}

                {/* ── 3 · La suma y la confirmación ──────────────── */}
                {stock && typeof total === "number" && (
                  <div className="mt-5 grid gap-4 border-t border-ds-border pt-5">
                    <dl className="grid gap-1 text-[14px]">
                      <div className="flex justify-between">
                        <dt className="text-ds-ink-3">
                          {stock.includesFranchise
                            ? t("Franquicia Digital (1 año) — incluida en el paquete")
                            : t("Franquicia Digital (1 año)")}
                        </dt>
                        <dd className="font-medium text-ds-ink">
                          {stock.includesFranchise
                            ? t("Incluida")
                            : formatPrice(franchisePriceCents, currency)}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-ds-ink-3">
                          {contentFor(stock.translations, locale).name}
                        </dt>
                        <dd className="font-medium text-ds-ink">
                          {formatPrice(stockPrice as number, currency)}
                        </dd>
                      </div>
                      <div className="mt-1 flex justify-between border-t border-ds-border pt-2">
                        <dt className="font-semibold text-ds-ink">{t("Total")}</dt>
                        <dd className="text-[18px] font-semibold text-ds-ink">
                          {formatPrice(total, currency)}
                        </dd>
                      </div>
                    </dl>

                    {confirmacion}
                  </div>
                )}
              </section>
            )}

              </div>

              {/* ── La columna DERECHA: comprar solo el paquete ────── */}
              {elegido && (
                <PaqueteElegidoCard
                  pkg={elegido}
                  currency={currency}
                  locale={locale}
                  onComprar={() => irAlCheckout(elegido, false)}
                />
              )}
            </div>

            {modalOpen && (
              <WholesaleStockModal
                currency={currency}
                locale={locale}
                onClose={() => setModalOpen(false)}
                onSelect={(pkg) => {
                  setStock(pkg);
                  setModalOpen(false);
                }}
              />
            )}
          </>
        )}
      </div>
    </ShopLayout>
  );
}

/** El paquete ya elegido, con la puerta para cambiarlo. */
function StockElegido({
  stock,
  currency,
  locale,
  onChange,
}: {
  stock: Package;
  currency: ReturnType<typeof useShopCurrency>[0];
  locale: ReturnType<typeof localeOf>;
  onChange: () => void;
}) {
  const { t } = useTranslation();
  const content = contentFor(stock.translations, locale);
  const price = precioConFranquicia(stock, currency);

  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-ds-violet-pop bg-ds-lavender-bg-2/50 p-4">
      <div>
        <p className="text-[15px] font-semibold text-ds-ink">{content.name}</p>
        <p className="text-[13px] text-ds-ink-3">
          {t("{{pv}} PV · {{off}}% de descuento", {
            pv: stock.volumePoints,
            off: effectiveDiscountPercent(stock, currency, price),
          })}
        </p>
      </div>
      <div className="flex items-center gap-3">
        <p className="text-[16px] font-semibold text-ds-ink">
          {typeof price === "number" ? formatPrice(price, currency) : "—"}
        </p>
        <Button variant="secondary" size="sm" onClick={onChange}>
          {t("Cambiar")}
        </Button>
      </div>
    </div>
  );
}

